import { type MultipleQueriesQuery } from '@algolia/client-search';
import { type SearchRequest as ElasticSearchRequest } from '@elastic/elasticsearch/lib/api/types';
import { merge } from 'merge-anything';

import {
	type BettingInsightsResultAttribute,
	type BettingSplitsIndex,
} from '~/configuration/betting-insights/types.ts';
import { convertSortingConfigToSortingValue } from '~/utils/convertSortingConfigToSortingValue.ts';
import { splitFacetFilter } from '~/utils/splitFacetFilter';

import { type BettingInsightsElasticSearch } from '../elasticsearch/BettingInsightsElasticSearch';

export type SearchRequest = {
	body: ElasticSearchRequest;
	indexName: string;
	request: MultipleQueriesQuery;
};

export type BettingSplitsFacetFilter =
	`${BettingInsightsResultAttribute<BettingSplitsIndex>}:${string}`;

export const bettingSplitsQueryFactory =
	(config: BettingInsightsElasticSearch<BettingSplitsIndex>) =>
	(searchRequest: SearchRequest): SearchRequest => {
		const indexName = searchRequest.indexName;

		let facetFilters: BettingSplitsFacetFilter[][] =
			(searchRequest.request.params
				?.facetFilters as BettingSplitsFacetFilter[][]) ?? [];
		if (!Array.isArray(facetFilters)) {
			facetFilters = [facetFilters];
		}

		const currentRefinements = facetFilters.map(([facetFilter]) => {
			const [attribute, label] = splitFacetFilter<
				BettingSplitsIndex,
				BettingSplitsFacetFilter
			>(facetFilter);
			return {
				attribute,
				refinements: [{ value: label }],
			};
		});

		const sorting = config.sorting({ currentRefinements });
		const defaultSort = sorting.options[0];

		if (!defaultSort) {
			throw new Error('Default sort not found');
		}

		const activeSort =
			sorting.options.find(
				sort =>
					`${indexName}${convertSortingConfigToSortingValue(sort)}` ===
					searchRequest.request.indexName,
			) ?? defaultSort;

		const order = {
			[activeSort.field]: activeSort.order,
		};

		const sortAggregate = {
			[activeSort.field]: {
				max: {
					field: activeSort.field,
				},
			},
		};

		const query = merge(searchRequest, {
			body: {
				size: 0,
				aggs: {
					subevents: {
						terms: {
							field: 'subeventId',
							size: 10000,
							order,
						},
						aggs: {
							...sortAggregate,
							markets: {
								terms: {
									field: 'marketId',
									size: 10000,
								},
								aggs: {
									bets: {
										terms: {
											field: 'betId',
											size: 10000,
										},
										aggs: {
											best_odds: {
												top_hits: {
													sort: {
														oddsDecimal: 'desc' as const,
													},
													size: 1,
												},
											},
										},
									},
								},
							},
						},
					},
				},
			},
		});

		return query;
	};
